import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import DatePicker from 'react-datepicker'
import Select from 'react-select'
import * as Yup from 'yup'
import { Formik, Form } from 'formik'
import { useSelector } from 'react-redux'

import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import API from '../../../api/api'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import Loader from '../../Loader/Loader'
import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'
import IglooWorkStaffSection from './IglooWorkStaffSection'
import RemoteLockSection from '../../Clients/ClientProfileView/IntegrationsSection/RemoteLockSection'

import { saltoSpaceDropdownStyles, getSelectedItemsValue, getConfirmationText } from './helpers'

const ManagementUserIntegrationsForm = ({ managementUserData, userBuildings = [] }) => {
  const { t } = useTranslation(['management-users', 'common'])
  const activeBuilding = useSelector(selectActiveBuilding)

  const {
    user: { email },
  } = managementUserData

  const isSaltoSpaceAvailable =
    (activeBuilding &&
      activeBuilding.saltoSpaceAvailable &&
      activeBuilding.keylessAccessForStaffAvailable) ||
    false

  // iglooWorkStaffAccessAvailable is the sole flag the Igloo staff endpoints check. Requiring both
  // it and keylessAccessForStaffAvailable would hide this block on a property that has Igloo staff
  // access on but RemoteLock staff access off.
  const isIglooWorkStaffAvailable =
    (activeBuilding &&
      activeBuilding.iglooWorkAvailable &&
      activeBuilding.iglooWorkStaffAccessAvailable) ||
    false

  const isRemoteLockAvailable =
    (activeBuilding &&
      activeBuilding.remoteLockAvailable &&
      activeBuilding.keylessAccessForStaffAvailable) ||
    false

  const getCurrentDate = () => new Date()

  const { displaySnackbar } = useSnackbar()
  const [isConnecting, setIsConnecting] = useState(false)
  const [isCreatingNewUser, setIsCreatingNewUser] = useState(false)
  const [isUserConnectedToSaltoSpace, setIsUserConnectedToSaltoSpace] = useState(false)
  const [isSaltoSpaceLoading, setIsSaltoSpaceLoading] = useState(false)

  const [saltoSpaceDoors, setSaltoSpaceDoors] = useState([])
  const [saltoSpaceGroups, setSaltoSpaceGroups] = useState([])
  const [saltoSpaceAccessLevels, setSaltoSpaceAccessLevels] = useState([])
  const [saltoSpaceInitialValues, setSaltoSpaceInitialValues] = useState({
    doors: [],
    groups: [],
    accessLevels: [],
    expirationDate: undefined,
  })
  const [isConnectUserPopupVisible, setIsConnectUserPopupVisible] = useState(false)
  const [isCreateNewUserPopupVisible, setIsCreateNewUserPopupVisible] = useState(false)
  const [uniqueSaltoUsers, setUniqueSaltoUsers] = useState([])
  const [selectedUserId, setSelectedUserId] = useState(null)

  const SaltoSpaceSchema = Yup.object().shape({
    selectedDoors: Yup.array().of(Yup.string()),
    selectedGroups: Yup.array().of(Yup.string()),
    selectedAccessLevels: Yup.array().of(Yup.string()),
    expirationDate: Yup.number(),
  })

  const initialValues = {
    saltoSpaceDoors: saltoSpaceDoors || [],
    saltoSpaceGroups: saltoSpaceGroups || [],
    saltoSpaceAccessLevels: saltoSpaceAccessLevels || [],
    expirationDate: saltoSpaceInitialValues.expirationDate,
    selectedDoors: saltoSpaceInitialValues.doors,
    selectedGroups: saltoSpaceInitialValues.groups,
    selectedAccessLevels: saltoSpaceInitialValues.accessLevels,
    selectedGroupsNames: [],
    selectedAccessLevelsNames: [],
    confirmed: false,
  }

  const fetchSaltoSpaceAccessData = async () => {
    try {
      const { data } = await API.getBuildingSaltoSpaceAccessPoints(activeBuilding.id)
      setSaltoSpaceDoors(data.doors)
      setSaltoSpaceGroups(data.permissionGroups)
      setSaltoSpaceAccessLevels(data.accessLevels)
    } catch {
      displaySnackbar(t('management-users:integrations.someErrorOccurred'), true)
    }
  }

  const getSaltoSpaceInitialValues = async () => {
    try {
      const { data } = await API.getSaltoSpaceUser(email)
      setSaltoSpaceInitialValues({
        doors: data.doorIds,
        groups: data.permissionGroupIds,
        accessLevels: data.accessLevelIds,
        expirationDate: data.userExpiration ? new Date(data.userExpiration).getTime() : undefined,
      })
    } catch (error) {
      displaySnackbar(error.message || t('management-users:integrations.someErrorOccurred'), true)
    }
  }

  const handleSaltoSpaceSubmit = async (
    selectedDoors,
    selectedGroups,
    selectedAccessLevels,
    expirationDate,
    confirmed,
  ) => {
    if (!confirmed) return
    try {
      setIsConnectUserPopupVisible(false)
      await API.updateSaltoSpaceUser(
        selectedDoors,
        selectedGroups,
        selectedAccessLevels,
        expirationDate,
        email,
      )
      displaySnackbar(t('management-users:integrations.accessGranted'))

      setSaltoSpaceInitialValues({
        doors: selectedDoors,
        groups: selectedGroups,
        accessLevels: selectedAccessLevels,
        expirationDate,
      })
    } catch {
      displaySnackbar(t('management-users:integrations.grantAccessError'), true)
    }
  }

  const fetchDisconnectedSaltoSpaceUsers = async () => {
    try {
      const { data = [] } = await API.getDisconnectedSaltoSpaceUsers(activeBuilding.id)
      setUniqueSaltoUsers(data)
    } catch (err) {
      console.error(err)
    }
  }

  const handleCreateNewUser = async () => {
    try {
      setIsCreatingNewUser(true)
      await API.createSaltoSpaceUser(email, activeBuilding.id)
      setIsUserConnectedToSaltoSpace(true)
      displaySnackbar(t('management-users:integrations.createNewSaltoUserSuccess'))
    } catch {
      displaySnackbar(t('management-users:integrations.createNewSaltoUserError'), true)
    } finally {
      setIsCreatingNewUser(false)
      setIsCreateNewUserPopupVisible(false)
    }
  }

  const handleConnectToSaltoSpace = async () => {
    try {
      setIsConnecting(true)
      await API.connectToSaltoSpace(email, activeBuilding.id, selectedUserId)
      setIsUserConnectedToSaltoSpace(true)
    } catch (error) {
      if (!selectedUserId && error.response.data && error.response.data.errCode === 422) {
        await fetchDisconnectedSaltoSpaceUsers()
      } else {
        displaySnackbar(t('management-users:integrations.connectToSaltoSpaceError'), true)
      }
    } finally {
      setIsConnecting(false)
    }
  }

  const checkIfUserIsConnectedToSaltoSpace = async () => {
    try {
      setIsSaltoSpaceLoading(true)
      const { data } = await API.isSaltoSpaceUserConnected(email)
      setIsUserConnectedToSaltoSpace(data)
    } catch (error) {
      console.error(error)
    } finally {
      setIsSaltoSpaceLoading(false)
    }
  }

  useEffect(() => {
    if (isUserConnectedToSaltoSpace) {
      fetchSaltoSpaceAccessData()
      getSaltoSpaceInitialValues()
    }
  }, [isUserConnectedToSaltoSpace])

  useEffect(() => {
    if (isSaltoSpaceAvailable) {
      checkIfUserIsConnectedToSaltoSpace()
    }
  }, [isSaltoSpaceAvailable])

  if (!managementUserData?.id) {
    return null
  }

  if (!isSaltoSpaceAvailable && !isRemoteLockAvailable && !isIglooWorkStaffAvailable) {
    return null
  }

  if (isSaltoSpaceLoading && !isRemoteLockAvailable && !isIglooWorkStaffAvailable) {
    return (
      <div className="integrations management-user-integrations">
        <div className="information-element">
          <Loader />
        </div>
      </div>
    )
  }

  return (
    <div className="integrations management-user-integrations">
      {/* The section-level guard above only returns null when EVERY integration is unavailable, so
          without this gate a RemoteLock-only building renders an empty "Salto Space / Create New
          User" panel for a provider it does not have. */}
      {isSaltoSpaceAvailable && (
        <div className="information-element">
          {isUserConnectedToSaltoSpace ? (
            <Formik
              initialValues={initialValues}
              enableReinitialize
              validationSchema={SaltoSpaceSchema}
              onSubmit={({
                selectedDoors,
                selectedGroups,
                selectedAccessLevels,
                expirationDate,
                confirmed,
              }) =>
                handleSaltoSpaceSubmit(
                  selectedDoors,
                  selectedGroups,
                  selectedAccessLevels,
                  expirationDate,
                  confirmed,
                )
              }
            >
              {({
                values,
                errors,
                touched,
                isSubmitting,
                setFieldValue,
                handleSubmit,
                resetForm,
              }) => {
                const hasError = (field) => errors[field] && touched[field]
                const { selectedDoors, selectedGroups, selectedAccessLevels } = values

                const doorValue = getSelectedItemsValue(selectedDoors, saltoSpaceDoors)
                const groupValue = getSelectedItemsValue(selectedGroups, saltoSpaceGroups)
                const accessLevelValue = getSelectedItemsValue(
                  selectedAccessLevels,
                  saltoSpaceAccessLevels,
                )
                const confirmationText = getConfirmationText(t, managementUserData, values)

                const onChangeDoor = (selectedOptions) => {
                  const saltoDoorIds = selectedOptions
                    ? selectedOptions.map((option) => option.saltoId)
                    : []
                  setFieldValue('selectedDoors', saltoDoorIds)
                }

                const onChangeGroup = (selectedOptions) => {
                  const { ids, names } = selectedOptions.reduce(
                    (acc, option) => {
                      acc.ids.push(option.saltoId)
                      acc.names.push(option.name)
                      return acc
                    },
                    { ids: [], names: [] },
                  )

                  setFieldValue('selectedGroups', ids)
                  setFieldValue('selectedGroupsNames', names)
                }

                const onChangeAccessLevel = (selectedOptions) => {
                  const { ids, names } = selectedOptions.reduce(
                    (acc, option) => {
                      acc.ids.push(option.saltoId)
                      acc.names.push(option.name)
                      return acc
                    },
                    { ids: [], names: [] },
                  )

                  setFieldValue('selectedAccessLevels', ids)
                  setFieldValue('selectedAccessLevelsNames', names)
                }

                const onChangeExpirationDate = (selectedDate) => {
                  const date = new Date(selectedDate)
                  return setFieldValue('expirationDate', date.getTime() || '')
                }

                const handleConfirm = () => {
                  setFieldValue('confirmed', true)
                  setIsConnectUserPopupVisible(true)
                }

                return (
                  <Form>
                    <div className="columns">
                      <div className="description-column">
                        <div className="element-name element-margin-top">
                          {t('management-users:integrations.saltoSpaceLabel')}
                        </div>
                        <div className="dropdown-buttons-container">
                          <div className="dropdown-buttons-container-row">
                            <div className="dropdown-container">
                              <div className="dropdown-header">
                                {t('management-users:integrations.doorHeader')}
                              </div>
                              <Select
                                styles={saltoSpaceDropdownStyles(hasError('selectedDoors'))}
                                classNamePrefix="dropdown-button"
                                options={values.saltoSpaceDoors}
                                getOptionLabel={(option) => option.name.toString()}
                                getOptionValue={(option) => option.saltoId}
                                value={doorValue}
                                onChange={(selectedOptions) => onChangeDoor(selectedOptions)}
                                placeholder={t(
                                  'management-users:integrations.selectDoorPlaceholder',
                                )}
                                isClearable
                                isMulti
                                closeMenuOnSelect={false}
                              />
                              {hasError('selectedDoors') && (
                                <div className="error-validation">{errors.selectedDoors}</div>
                              )}
                            </div>
                            <div className="dropdown-container">
                              <div className="dropdown-header">
                                {t('management-users:integrations.zoneHeader')}
                              </div>
                              <Select
                                styles={saltoSpaceDropdownStyles(hasError('selectedGroups'))}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                options={values.saltoSpaceGroups}
                                getOptionLabel={(option) => option.name.toString()}
                                getOptionValue={(option) => option.saltoId}
                                value={groupValue}
                                onChange={(selectedOptions) => onChangeGroup(selectedOptions)}
                                placeholder={t(
                                  'management-users:integrations.selectZonePlaceholder',
                                )}
                                isClearable
                                isMulti
                                closeMenuOnSelect={false}
                              />
                              {hasError('selectedGroups') && (
                                <div className="error-validation">{errors.selectedGroups}</div>
                              )}
                            </div>
                          </div>
                          <div className="dropdown-buttons-container-row">
                            <div className="dropdown-container">
                              <div className="dropdown-header">
                                {t('management-users:integrations.accessLevelHeader')}
                              </div>
                              <Select
                                styles={saltoSpaceDropdownStyles(hasError('selectedAccessLevels'))}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                options={values.saltoSpaceAccessLevels}
                                getOptionLabel={(option) => option.name.toString()}
                                getOptionValue={(option) => option.saltoId}
                                value={accessLevelValue}
                                onChange={(selectedOptions) => onChangeAccessLevel(selectedOptions)}
                                placeholder={t(
                                  'management-users:integrations.selectAccessLevelPlaceholder',
                                )}
                                isClearable
                                isMulti
                                closeMenuOnSelect={false}
                              />
                              {hasError('selectedAccessLevels') && (
                                <div className="error-validation">
                                  {errors.selectedAccessLevels}
                                </div>
                              )}
                            </div>
                            <div className="dropdown-container">
                              <div className="dropdown-header">
                                {t('management-users:integrations.expirationDateHeader')}
                              </div>
                              <DatePicker
                                className={`dropdown-button ${
                                  hasError('expirationDate') && 'dropdown-button-error-color'
                                }`}
                                placeholderText={t(
                                  'management-users:integrations.expirationDatePlaceholder',
                                )}
                                selected={values.expirationDate || null}
                                onChange={(selectedDate) => onChangeExpirationDate(selectedDate)}
                                minDate={getCurrentDate()}
                                isClearable
                              />
                              {hasError('expirationDate') && (
                                <div className="error-validation">{errors.expirationDate}</div>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          className={`confirm-button button-primary button ${
                            isSubmitting ? 'is-loading' : ''
                          }`}
                          disabled={isSubmitting}
                          onClick={() => handleConfirm()}
                        >
                          {t('common:actions.confirm')}
                        </button>
                      </div>
                    </div>
                    <ConfirmPopup
                      title={t('management-users:integrations.cautionTitle')}
                      body={confirmationText}
                      cancelText={'Close'}
                      confirmText={'Yes, confirm'}
                      isOpen={isConnectUserPopupVisible}
                      cancelCallback={() => {
                        resetForm()
                        setIsConnectUserPopupVisible(false)
                      }}
                      confirmCallback={handleSubmit}
                      className={'is-caution'}
                    />
                  </Form>
                )
              }}
            </Formik>
          ) : (
            <div className="information-element-row">
              <div className="element-name">
                {t('management-users:integrations.saltoSpaceLabel')}
              </div>
              {uniqueSaltoUsers.length > 0 ? (
                <div className="columns">
                  <div className="column column-right">
                    <Select
                      styles={{
                        ...saltoSpaceDropdownStyles(),
                        control: (styles) => ({
                          ...styles,
                          minWidth: '250px',
                        }),
                      }}
                      options={uniqueSaltoUsers.map((user) => ({
                        label: user.name,
                        value: user.saltoUserId,
                      }))}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.value}
                      value={uniqueSaltoUsers.find((user) => user.value === selectedUserId)}
                      onChange={(selectedOption) =>
                        selectedOption
                          ? setSelectedUserId(selectedOption.value)
                          : setSelectedUserId(null)
                      }
                      placeholder={t('management-users:integrations.selectUserPlaceholder')}
                      isClearable
                      closeMenuOnSelect
                    />
                    <button
                      className={`button button-primary ${isConnecting ? 'is-loading' : ''}`}
                      onClick={handleConnectToSaltoSpace}
                      disabled={isConnecting || !selectedUserId}
                    >
                      {isConnecting
                        ? t('management-users:integrations.connectingButton')
                        : t('management-users:integrations.connectToSaltoSpaceButton')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="columns">
                  <div className="column column-right">
                    <button
                      className={`button button-primary ${isCreatingNewUser ? 'is-loading' : ''}`}
                      onClick={() => setIsCreateNewUserPopupVisible(true)}
                      disabled={isConnecting || isCreatingNewUser}
                      type="button"
                    >
                      {isCreatingNewUser
                        ? t('management-users:integrations.creatingButton')
                        : t('management-users:integrations.createNewUserButton')}
                    </button>
                    <span>{t('management-users:integrations.orConnector')}</span>
                    <button
                      className={`button button-primary ${isConnecting ? 'is-loading' : ''}`}
                      onClick={handleConnectToSaltoSpace}
                      disabled={isConnecting || isCreatingNewUser}
                      type="button"
                    >
                      {isConnecting
                        ? t('management-users:integrations.connectingButton')
                        : t('management-users:integrations.connectExistingButton')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <ConfirmPopup
            title={t('management-users:integrations.cautionTitle')}
            body={t('management-users:integrations.createNewUserConfirmBody')}
            isOpen={isCreateNewUserPopupVisible}
            cancelText={t('common:actions.close')}
            confirmText={t('management-users:integrations.confirmCreateYesText')}
            cancelCallback={() => setIsCreateNewUserPopupVisible(false)}
            confirmCallback={handleCreateNewUser}
            className="is-caution"
            isLoading={isCreatingNewUser}
          />
        </div>
      )}
      <RemoteLockSection
        isAvailable={isRemoteLockAvailable}
        isFobAvailable={false}
        isIntegratedId={managementUserData.remoteLockId || null}
        buildingId={activeBuilding?.id}
        clientId={managementUserData.id}
        userId={managementUserData.user.id}
        showExpiry
      />
      <IglooWorkStaffSection
        isAvailable={isIglooWorkStaffAvailable}
        buildingId={activeBuilding?.id}
        userId={managementUserData.user.id}
        staffName={
          [managementUserData.user.firstName, managementUserData.user.lastName]
            .filter(Boolean)
            .join(' ') || email
        }
      />
    </div>
  )
}

ManagementUserIntegrationsForm.propTypes = {
  managementUserData: PropTypes.object,
  userBuildings: PropTypes.array,
}

export default ManagementUserIntegrationsForm
